import { useEffect, useRef } from 'react';
import { MeetingStatus } from 'amazon-chime-sdk-component-library-react';

const useVideoInputAutoSelect = ({
  meetingStatus,
  meetingManager,
  initialVideoInputDeviceId,
  activeCameraDeviceId,
  setActiveCameraDeviceId,
  setCameraDevices,
  localCameraOn,
  applyCameraQualityProfile,
}) => {
  const knownVideoInputIdsRef = useRef(new Set());
  const hasSeededKnownVideoInputsRef = useRef(false);

  useEffect(() => {
    if (meetingStatus !== MeetingStatus.Succeeded) {
      return () => {};
    }

    let cancelled = false;
    let applying = false;
    let pending = false;
    let deviceChangeTimer = null;

    const syncAndAutoSelectVideoInput = async () => {
      if (cancelled || applying) {
        pending = true;
        return;
      }

      applying = true;
      try {
        const av = meetingManager.meetingSession?.audioVideo;
        if (!av) {
          return;
        }

        const devices = await av.listVideoInputDevices();
        if (!devices?.length || cancelled) {
          return;
        }

        setCameraDevices(devices);

        const currentIds = new Set(devices.map((device) => device.deviceId));
        const previousIds = hasSeededKnownVideoInputsRef.current
          ? knownVideoInputIdsRef.current
          : currentIds;
        if (!hasSeededKnownVideoInputsRef.current) {
          knownVideoInputIdsRef.current = currentIds;
          hasSeededKnownVideoInputsRef.current = true;
        }
        const addedDevices = devices.filter((device) => !previousIds.has(device.deviceId));

        const activeExists = activeCameraDeviceId && currentIds.has(activeCameraDeviceId);
        const hasInitial = !!(initialVideoInputDeviceId && currentIds.has(initialVideoInputDeviceId));
        const fallbackDeviceId = devices[0].deviceId;
        const nextDeviceId = addedDevices.length
          ? addedDevices[addedDevices.length - 1].deviceId
          : (activeExists ? activeCameraDeviceId : (hasInitial ? initialVideoInputDeviceId : fallbackDeviceId));

        knownVideoInputIdsRef.current = currentIds;

        if (!nextDeviceId || nextDeviceId === activeCameraDeviceId) {
          return;
        }

        if (typeof meetingManager.startVideoInputDevice === 'function') {
          await meetingManager.startVideoInputDevice(nextDeviceId);
        } else {
          await av.chooseVideoInputDevice(nextDeviceId);
        }

        applyCameraQualityProfile();
        setActiveCameraDeviceId(nextDeviceId);

        if (localCameraOn) {
          av.startLocalVideoTile();
        }
      } catch (_error) {
        // Non-fatal.
      } finally {
        applying = false;
        if (pending && !cancelled) {
          pending = false;
          syncAndAutoSelectVideoInput();
        }
      }
    };

    syncAndAutoSelectVideoInput();

    const handleDeviceChange = () => {
      if (deviceChangeTimer) {
        clearTimeout(deviceChangeTimer);
      }
      deviceChangeTimer = setTimeout(() => {
        deviceChangeTimer = null;
        syncAndAutoSelectVideoInput();
      }, 180);
    };

    if (typeof navigator !== 'undefined' && navigator.mediaDevices?.addEventListener) {
      navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    }

    return () => {
      cancelled = true;
      if (deviceChangeTimer) {
        clearTimeout(deviceChangeTimer);
        deviceChangeTimer = null;
      }
      if (typeof navigator !== 'undefined' && navigator.mediaDevices?.removeEventListener) {
        navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
      }
    };
  }, [
    meetingStatus,
    meetingManager,
    initialVideoInputDeviceId,
    activeCameraDeviceId,
    setActiveCameraDeviceId,
    setCameraDevices,
    localCameraOn,
    applyCameraQualityProfile,
  ]);
};

export default useVideoInputAutoSelect;
